import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { baseUrl } from '../components/BaseUrl';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Vimeo from '@u-wave/react-vimeo';
import Swal from "sweetalert2";
 

const Home = () => {
  const [currentBg, setCurrentBg] = useState(0);
  const [fetching, setFetching] = useState(false);
  const [videos, setVideos] = useState([]);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const [quizzes, setQuizzes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [quizzesLoading, setQuizzesLoading] = useState(false);
  const [videosLoading, setVideosLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState(null);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  
  const videosRef = useRef(null);
  const coursesRef = useRef(null);
  const quizzesRef = useRef(null);

  const videoModalRef = useRef(null);
  const videoPlayerRef = useRef(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const navigate = useNavigate();

  const heroImages = [
    'https://app.codeflarelimited.com/elearning/assets/sliding/enter.webp',
    'https://app.codeflarelimited.com/assets/sliding/react.webp',
    'https://app.codeflarelimited.com/elearning/assets/sliding/css.webp',
    'https://app.codeflarelimited.com/elearning/assets/sliding/mongodb.webp',
  ];

  // Add this function to handle course clicks
  const handleCourseClick = (course) => {
    if (!course.available) {
      Swal.fire({
        title: "Not Available",
        text: "We're working to make this course available to all learners.",
        icon: "error",
        confirmButtonText: "OK",
        backdrop: `
        rgba(143, 70, 0, 0.3)
        url("")
        left top
        no-repeat
      `
            });
    } else {
      navigate(`/course-description/${course.id}`);
    }
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isModalOpen) {
        closeVideoModal();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isModalOpen])

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen])

  useEffect(() => {
    getVideos();
    getQuizzes();
    getCourses();
  }, []);

 // Check if user is logged in
  useEffect(() => {
    checkLoginStatus();
  }, []);

  const checkLoginStatus = () => {
    const token = localStorage.getItem('codefussion-login-token');
    const user = localStorage.getItem('codefussion-student-first-name');
    if (token && user) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
  };

  const handleLogout = () => {
    // Clear all auth-related items from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userRole');
    localStorage.removeItem('loginTime');
    
    // Update state
    setIsLoggedIn(false);
    
    // Redirect to home page
    navigate('/');
    
    // Optional: reload the page to reset all state
    window.location.reload();
  };

const getVideos = async () => {
  try {
    setVideosLoading(true);
    const response = await fetch(`${baseUrl}get-videos.php`);
    if (!response.ok) throw new Error("Error fetching videos");
    const videosData = await response.json();
    
    const availableVideos = videosData
      .filter(video => video.available === true || video.available === "true")
      .map(video => ({
        id: video.id,
        video_id: video.video_id,
        title: video.video_title,
        description: video.video_description,
        duration: video.duration,
        level: video.level,
        category: video.category,
        thumbnail: video.image,
        available: Boolean(video.available),
        subscription_required: Boolean(video.subscription_required)
      }))
      .slice(0, 10);
    
    setVideos(availableVideos);
  } catch (error) {
    console.error("Error fetching videos:", error);
    setError(error.message);
  } finally {
    setVideosLoading(false);
  }
};

  const getQuizzes = async () => {
    try {
      setQuizzesLoading(true);
      const response = await fetch(`${baseUrl}get_quizzes.php`);
      if (!response.ok) throw new Error("Error fetching quizzes");
      const result = await response.json();
      
      if (result.status === 'success') {
        const formattedQuizzes = result.data
          .map(quiz => ({
            id: quiz.id,
            title: quiz.title,
            questions: quiz.questions_count,
            duration: quiz.duration,
            difficulty: quiz.difficulty,
            taken: 0,
            thumbnail: quiz.image || quiz.image_url
          }))
          .slice(0, 10);
        
        setQuizzes(formattedQuizzes);
      } else {
        throw new Error(result.message || "Failed to fetch quizzes");
      }
    } catch (error) {
      console.error("Error fetching quizzes:", error);
      setError(error.message);
    } finally {
      setQuizzesLoading(false);
    }
  };

  const getCourses = async () => {
    try {
      setCoursesLoading(true);
      const response = await fetch(`${baseUrl}get_courses.php`);
      if (!response.ok) throw new Error("Error fetching courses");
      const coursesData = await response.json();
      
      const formattedCourses = coursesData
        .map(course => ({
          id: course.id,
          title: course.course_title,
          description: course.course_description,
          lessons: course.course_url ? course.course_url.length : 0,
          duration: '0 hours',
          level: 'Beginner',
          progress: 0,
          thumbnail: course.image || 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80',
          category: course.category,
          available: Boolean(course.available),
          subscription_required: Boolean(course.subscription_required)
        }))
        .slice(0, 10);
      
      setCourses(formattedCourses);
    } catch (error) {
      console.error("Error fetching courses:", error);
      setError(error.message);
    } finally {
      setCoursesLoading(false);
    }
  };

  // Fetch video details when modal opens
  const fetchVideoDetails = async (videoId) => {
    try {
      setVideoLoading(true);
      setVideoError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseUrl}get-video-id.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ id: videoId }),
      });

      if (!response.ok) throw new Error('Failed to fetch video details');
      
      const videoData = await response.json();
      
      if (videoData && videoData.length > 0) {
        setCurrentVideo(videoData[0]);
      } else {
        throw new Error('Video not found');
      }
    } catch (error) {
      console.error('Error fetching video:', error);
      setVideoError(error.message);
    } finally {
      setVideoLoading(false);
    }
  };

  // Open modal and fetch video
  const openVideoModal = async (video) => {
    setIsModalOpen(true);
    await fetchVideoDetails(video.video_id);
  };

  // Close modal
  const closeVideoModal = () => {
    setIsModalOpen(false);
    setCurrentVideo(null);
    setVideoError(null);
    setVideoProgress(0);
    setVideoDuration(0);
    if (videoPlayerRef.current) {
      // You might want to pause the video here if needed
    }
  }

  // Format duration to readable format
  const formatDuration = (duration) => {
    if (!duration) return '0:00';
    const cleanDuration = duration.replace(/\D/g, '');
    const minutes = Math.floor(parseInt(cleanDuration) / 60);
    const seconds = parseInt(cleanDuration) % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Vimeo event handlers
  const handleLoaded = (event) => {
    console.log('Video loaded successfully', event);
    setVideoLoading(false);
  };

  const handleError = (error) => {
    console.error('Error loading video:', error);
    setVideoError('Failed to load video. Please try again.');
    setVideoLoading(false);
  };

  const handleEnd = () => {
    console.log('Video ended');
  };

  const onProgress = (data) => {
    setVideoProgress(data.percent);
    setVideoDuration(data.duration);
  };

  // Extract Vimeo ID from URL for fallback
  const getVimeoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:vimeo\.com\/)(\d+)/);
    return match ? match[1] : null;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBg((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  const scroll = (ref, direction) => {
    if (ref.current) {
      const scrollAmount = 300;
      ref.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleScroll = (ref) => {
    if (ref.current) {
      const { scrollLeft, scrollWidth, clientWidth } = ref.current;
      if (scrollLeft > 10) {
        setShowScrollHint(false);
      }
    }
  };

  const handleTouchStart = (ref, e) => {
    if (ref.current) {
      ref.current.touchStartX = e.touches[0].clientX;
    }
  };

  const handleTouchMove = (ref, e) => {
    if (!ref.current || !ref.current.touchStartX) return;
    
    const touchEndX = e.touches[0].clientX;
    const diff = ref.current.touchStartX - touchEndX;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        scroll(ref, 'right');
      } else {
        scroll(ref, 'left');
      }
      ref.current.touchStartX = null;
    }
  };

  const LoadingSpinner = () => (
    <div className="loading-spinner">
      <div className="spinner"></div>
      <p>Loading resources...</p>
    </div>
  );

  const SeeMoreCard = ({ to, section }) => (
    <div className="see-more-card">
      <Link to={to}>
        <div className="see-more-content">
          <div className="see-more-icon text-light">+</div>
          <h3 className="text-light">See More {section}</h3>
          <p className="text-light">Explore all available content</p>
          <div className="see-more-arrow text-light">→</div>
        </div>
      </Link>
    </div>
  );

  return (
    <div>

      <Navbar 
        isLoggedIn={isLoggedIn} 
        onLogout={handleLogout}
        // isMenuOpen={isMenuOpen} 
        // setIsMenuOpen={setIsMenuOpen}
      />
 
    <div className="codecademy-style">
      {/* Updated Navbar with mobile menu props */}
      

      {/* {isMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setIsMenuOpen(false)}></div>
      )} */}

      {/* Video Modal */}
      {isModalOpen && (
        <div className="video-modal-overlay" onClick={closeVideoModal}>
          <div 
            className="video-modal-content" 
            ref={videoModalRef}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modern Header */}
            <div className="modal-header">
              <div className="modal-title-section">
                <h2 className="video-modal-title">
                  {currentVideo?.video_title || 'Video Preview'}
                </h2>
                <div className="video-meta-tags">
                  <span className="meta-tag level">{currentVideo?.level || 'All Levels'}</span>
                  <span className="meta-tag duration">
                    {currentVideo?.duration ? formatDuration(currentVideo.duration) : '0:00'}
                  </span>
                  <span className="meta-tag category">{currentVideo?.category || 'General'}</span>
                  {currentVideo?.subscription_required && (
                    <span className="meta-tag premium">Premium</span>
                  )}
                </div>
              </div>
              <button className="modal-close-btn" onClick={closeVideoModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Main Modal Content */}
            <div className="modal-body">
              {videoLoading ? (
                <div className="modal-loading-state">
                  <div className="loading-spinner-large"></div>
                  <p>Loading video preview...</p>
                </div>
              ) : videoError ? (
                <div className="modal-error-state">
                  <div className="error-illustration">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2"/>
                      <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </div>
                  <h3>Video preview not available</h3>
                  <p>{videoError}</p>
                  <button className="btn btn-primary" onClick={closeVideoModal}>
                    Close Preview
                  </button>
                </div>
              ) : currentVideo ? (
                <div className="video-content-container">
                  {/* Fixed Video Player Container */}
                  <div className="video-player-wrapper" ref={videoPlayerRef}>
                    <div className="video-player-container">
                      {currentVideo.video_url ? (
                        <Vimeo
                          video={currentVideo.video_url}
                          autoplay={true}
                          showTitle={false}
                          showPortrait={false}
                          responsive={true}
                          controls={true}
                          showByline={false}
                          loop={false}
                          playsInline={true}
                          onReady={handleLoaded}
                          onError={handleError}
                          onPlay={() => console.log('Video started playing')}
                          onPause={() => console.log('Video paused')}
                          onEnd={handleEnd}
                          onProgress={onProgress}
                          className="vimeo-player-modern"
                        />
                      ) : (
                        <div className="video-unavailable">
                          <div className="unavailable-illustration">
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                              <path d="M23 7L16 12L23 17V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <rect x="1" y="5" width="15" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                          </div>
                          <h4>Video Not Available</h4>
                          <p>This video content is currently unavailable.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Enhanced Video Information */}
                  <div className="video-info-panel">
                    <div className="info-section">
                      <h3>About This Video</h3>
                      <p className="video-description-modern">
                        {currentVideo.video_description || 'No description available for this video content.'}
                      </p>
                    </div>

                    <div className="action-section">
                      <div className="action-buttons">
                        <Link style={{ padding: 0 }} to={`/watch-video/${currentVideo.video_id}`}>
                        <button className="btn btn-primary">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M8 5V19L19 12L8 5Z" fill="currentColor"/>
                          </svg>
                          Start Full Course
                        </button>
                        </Link>
                        <button className="btn btn-outline">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path d="M19 21L12 16L5 21V5C5 4.46957 5.21071 3.96086 5.58579 3.58579C5.96086 3.21071 6.46957 3 7 3H17C17.5304 3 18.0391 3.21071 18.4142 3.58579C18.7893 3.96086 19 4.46957 19 5V21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Save for Later
                        </button>
                      </div>

                      <div className="video-stats-modern">
                        <div className="stat-item">
                          <span className="stat-label">Level</span>
                          <span className="stat-value">{currentVideo.level}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">Duration</span>
                          <span className="stat-value">{formatDuration(currentVideo.duration)}</span>
                        </div>
                        <div className="stat-item">
                          <span className="stat-label">Category</span>
                          <span className="stat-value">{currentVideo.category}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Hero Carousel - NO PADDING */}
      <section className="hero-carousel">
        <div className="carousel-container">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`carousel-slide ${index === currentBg ? 'active' : ''}`}
              style={{ backgroundImage: `url(${image})` }}
            >
              <div className="slide-overlay"></div>
              <div className="carousel-content">
                <h1>Unlock Your Potential</h1>
                <p>Interactive courses and projects to build real skills</p>
                <div className="carousel-actions">
                  <Link to="/dashboard" className="btn btn-primary text-center p-3">Start Learning Free</Link>
                  <Link to="/plans" className="btn btn-secondaryy">Go Pro</Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Main Content */}
      <div className="container-fluid px-0">
        {/* Video Courses Section */}
        <div className="section-header">
          <div className='course-container'>
            <div className="section-title"> 
              <h2>Videos</h2>
            </div>
            <div className="scroll-buttons-container">
              <button className="scrolling-btn" onClick={() => scroll(videosRef, 'left')}>‹</button>
              <button className="scrolling-btn" onClick={() => scroll(videosRef, 'right')}>›</button>
            </div>
          </div>
        </div>

        <section className="section videos-section">
          <div className="container">
            <div className="scroll-container-wrapper">
              <div className="scroll-indicator left-fade"></div>
              <div className="scroll-container">
                <div 
                  className="cards-grid" 
                  ref={videosRef}
                  onScroll={() => handleScroll(videosRef)}
                  onTouchStart={(e) => handleTouchStart(videosRef, e)}
                  onTouchMove={(e) => handleTouchMove(videosRef, e)}
                >
                  {videosLoading ? (
                    <div className="loading-state">
                      <LoadingSpinner />
                    </div>
                  ) : videos.length > 0 ? (
                    <>
                     {videos.map(video => (
  <div key={video.id} className="video-card">
    <div className="card-image-video">
      <img src={`${baseUrl}${video.thumbnail}`} alt={video.title} />
      <div className="play-overlay">
        <button 
          className="btn-card-outline"
          onClick={() => openVideoModal(video)}
        >
        <div className="play-icon">▶</div>
        </button>
      </div>
      <span className="duration">{formatDuration(video.duration)}</span>
      <div className="card-badge">{video.category}</div>
    </div>
    <div className="card-content">
      <h3>{video.title}</h3>
      <p className="video-card-description">
        {video.description || 'Watch this engaging tutorial to enhance your skills.'}
      </p>
      <div className="card-meta">
        <span className="level">{video.level}</span>
        <span className="views">• 2.5K views</span>
      </div>
      <div className="card-actions">
        <button 
          className="btn-card-outline"
          onClick={() => openVideoModal(video)}
        >
          Preview Course
        </button>
        <span className={video.subscription_required ? 'premium-text' : 'free-text'}>
          {video.subscription_required ? 'Premium' : 'Free'}
        </span>
      </div>
    </div>
  </div>
))}
                      <SeeMoreCard to="/videos" section="Videos" />
                    </>
                  ) : (
                    <div className="loading-state">
                      <p>No videos available</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="scroll-indicator right-fade"></div>
            </div>
          </div>
        </section>

        {/* Featured Courses Section */}
        <div className="section-header">
          <div className='course-container'>
            <div className="section-title"> 
              <h2>Readings</h2>
            </div>
            <div className="scroll-buttons-container">
              <button className="scrolling-btn" onClick={() => scroll(coursesRef, 'left')}>‹</button>
              <button className="scrolling-btn" onClick={() => scroll(coursesRef, 'right')}>›</button>
            </div>
          </div>
        </div>

        <section className="section courses-section">
          <div className="container">
            <div className="scroll-container-wrapper">
              <div className="scroll-indicator left-fade"></div>
              <div className="scroll-container">
                <div 
                  className="cards-grid" 
                  ref={coursesRef}
                  onScroll={() => handleScroll(coursesRef)}
                  onTouchStart={(e) => handleTouchStart(coursesRef, e)}
                  onTouchMove={(e) => handleTouchMove(coursesRef, e)}
                >
                  {coursesLoading ? (
                    <div className="loading-state">
                      <LoadingSpinner />
                    </div>
                  ) : courses.length > 0 ? (
                    <>
                      {courses.map(course => (
                        <div key={course.id} className="course-card">
                          <div className="card-image">
                            <img 
                              src={`${baseUrl}${course.thumbnail}`} 
                              alt={course.title}
                              onError={(e) => {
                                e.target.src = 'https://codeflarelimited.com/assets/images/codeflare-app.webp';
                              }}
                            />
                            <div className="card-badge">{course.category}</div>
                            {course.subscription_required && (
                              <div className="premium-badge crown"></div>
                            )}
                          </div>
                          <div className="card-content">
                            <h3>{course.title}</h3>
                            <p className="course-description">{course.description}</p>
                            <div className="card-meta">
                              <span className="lessons">{course.lessons} lessons</span>
                              <span className="duration">{course.duration}</span>
                            </div>
                            <div className="level-badge">{course.level}</div>
                            <div className="card-actions">
                              {/* MODIFIED: Added conditional click handler */}
                              <button 
                                className="btn-card"
                                onClick={() => handleCourseClick(course)}
                              >
                                {course.available ? 'Start Course' : 'Coming Soon'}
                              </button>
                              <span className={`progress-text ${course.subscription_required ? 'premium-text' : 'free-text'}`}>
                                {course.subscription_required ? 'Premium' : 'Free'}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                      <SeeMoreCard to="/course-catalogue" section="Courses" />
                    </>
                  ) : (
                    <div className="loading-state">
                      <p>No courses available</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="scroll-indicator right-fade"></div>
            </div>
          </div>
        </section>

        {/* Quizzes Section */}
        <div className="section-header">
          <div className='course-container'>
            <div className="section-title"> 
              <h2>Assessments</h2>
            </div>
            <div className="scroll-buttons-container">
              <button className="scrolling-btn" onClick={() => scroll(quizzesRef, 'left')}>‹</button>
              <button className="scrolling-btn" onClick={() => scroll(quizzesRef, 'right')}>›</button>
            </div>
          </div>
        </div>

        <section className="section quizzes-section">
          <div className="container">
            <div className="scroll-container-wrapper">
              <div className="scroll-indicator left-fade"></div>
              <div className="scroll-container">
                <div 
                  className="cards-grid" 
                  ref={quizzesRef}
                  onScroll={() => handleScroll(quizzesRef)}
                  onTouchStart={(e) => handleTouchStart(quizzesRef, e)}
                  onTouchMove={(e) => handleTouchMove(quizzesRef, e)}
                >
                  {quizzesLoading ? (
                    <div className="loading-state">
                      <LoadingSpinner />
                    </div>
                  ) : quizzes.length > 0 ? (
                    <>
                      {quizzes.map(quiz => (
                        <div key={quiz.id} className="quiz-card">
                          <div className="card-image">
                            <img src={`${quiz.thumbnail}`} alt={quiz.title} />
                            <div className={`difficulty-badge ${quiz.difficulty?.toLowerCase() || 'medium'}`}>
                              {quiz.difficulty || 'Medium'}
                            </div>
                          </div>
                          <div className="card-content">
                            <h3>{quiz.title}</h3>
                            <div className="card-meta">
                              <span>{quiz.questions} questions</span>
                              <span>{quiz.duration}</span>
                            </div>
                            <Link to="/quiz"><button className="btn-card">Take Assessment</button></Link>
                          </div>
                        </div>
                      ))}
                      <SeeMoreCard to="/quiz" section="Quizzes" />
                    </>
                  ) : (
                    <div className="loading-state">
                      <p>No quizzes available</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="scroll-indicator right-fade"></div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section cta-section">
          <div className="cta-content">
            <h2>Ready to start your learning journey?</h2>
            <p>Join thousands of students learning to code with Codeflare</p>
            <div className="cta-actions">
              <Link to="/create-account" className="btn btn-large btn-primary">Join for Free</Link>
              <Link to="/plans" className="btn btn-large btn-outline text-center" style={{ background: '#fdc500', color: '#000'}}>Go Pro</Link>
            </div>
          </div>
        </section>
      </div>
         </div>

      <Footer />

      <style jsx>{`
        /* Base Responsive Styles */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          max-width: 100%;
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }

        *::-webkit-scrollbar {
          display: none;  /* Chrome, Safari and Opera */
        }
        
        // .codecademy-style {
        //   color: #1a1a1a;
        //   line-height: 1.6;
        //   background-color: ghostwhite;
        //   font-family: 'Urbanist', sans-serif;
        //   overflow-x: hidden;
        //   width: 100%;
        //   padding-top: 70px; /* Account for fixed navbar */
        // }

        /* Import Urbanist Font */
        @import url('https://fonts.googleapis.com/css2?family=Urbanist:ital,wght@0,100..900;1,100..900&display=swap');
        
        .container-fluid {
          width: 100%;
          padding-left: 0 !important;
          padding-right: 0 !important;
          margin-right: auto;
          margin-left: auto;
          overflow-x: hidden;
        }

        .mobile-menu-overlay {
          position: fixed;
          top: 70px;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 999;
          display: none;
        }

        @media (max-width: 768px) {
          .mobile-menu-overlay {
            display: block;
          }
        }

        .loading-spinner {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          color: #666;
          width: 100%;
        }
        
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #0066ff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .loading-state {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          min-height: 200px;
        }
        
        /* Hero Carousel - NO PADDING ON MOBILE */
        .hero-carousel {
          position: relative;
          height: 70vh;
          min-height: 400px;
          max-height: 600px;
          overflow: hidden;
          width: 100%;
          margin: 0;
          padding: 0;
        }
        
        .carousel-container {
          position: relative;
          width: 100%;
          height: 100%;
          margin: 0;
          padding: 0;
        }
        
        .carousel-slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          opacity: 0;
          transition: opacity 0.8s ease-in-out;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0;
          padding: 0;
        }
        
        .carousel-slide.active {
          opacity: 1;
        }
        
        .slide-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(0,51,153,0.7) 100%);
          margin: 0;
          padding: 0;
        }
        
        .carousel-content {
          position: relative;
          z-index: 2;
          text-align: center;
          color: white;
          max-width: 90%;
          padding: 0;
          margin: 0 auto;
        }
        
        .carousel-content h1 {
          font-size: clamp(2rem, 5vw, 3.5rem);
          font-weight: 700;
          margin-bottom: 1rem;
          line-height: 1.1;
        }
        
        .carousel-content p {
          font-size: clamp(1rem, 3vw, 1.3rem);
          margin-bottom: 2rem;
          opacity: 0.9;
        }
        
        .carousel-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
        }
        
        /* Buttons */
        .btn {
          padding: 12px 24px;
          border-radius: 6px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.2s;
          border: none;
          cursor: pointer;
          text-align: center;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: clamp(0.9rem, 2vw, 1rem);
          min-width: fit-content;
        }
        
        .btn-primary {
          background: #145AE6;
          color: white;
        }
        
        .btn-primary:hover {
          background: #145AE6;
          transform: translateY(-2px);
        }
        
        .btn-secondaryy {
         background: #fdc500;
          color: black;
        }
        
        .btn-secondaryy:hover {
          background: white;
          color: #0066ff;
        }
        
        .btn-large {
          padding: 15px 30px;
          font-size: clamp(1rem, 2.5vw, 1.1rem);
        }
        
        .btn-outline {
          background: transparent;
          border: 2px solid white;
          color: white;
        }
        
        .btn-outline:hover {
          background: white;
          color: #0066ff;
        }

        /* Course Container */
        .course-container {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          margin-bottom: -72px;
          width: 100%;
          padding: 0 5%;
        }

        /* Scrolling buttons */
        .scrolling-btn {
          background: #0066ff;
          width: 44px;
          height: 44px;
          color: white;
          font-size: 1.5rem;
          border-radius: 10px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
          margin-left: 8px;
          flex-shrink: 0;
        }

        .scrolling-btn:hover {
          background: #0052cc;
          transform: scale(1.05);
        }

        /* Premium Buttons */
        .btn-disabled {
          background: #ccc !important;
          cursor: not-allowed !important;
        }

        .btn-disabled:hover {
          transform: none !important;
          background: #ccc !important;
        }

        .premium-text {
          color: #FFBF00;
          font-weight: bold;
        }

        .free-text {
          color: #00cc88;
          font-weight: bold;
        }

        .scroll-buttons-container {
          display: flex;
          gap: 5px;
          flex-shrink: 0;
        }

        .premium-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          background: linear-gradient(135deg, #FFD700, #FFA500);
          color: #000;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.7rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 1px;
          box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);
          border: 2px solid #FFF;
          display: flex;
          align-items: center;
          gap: 6px;
          z-index: 2;
          transform: translateY(0);
          transition: all 0.3s ease;
        }

        .premium-badge::before {
          content: "👑";
          font-size: 0.7rem;
        }

        .course-card:hover .premium-badge {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(255, 215, 0, 0.6);
        }

        .course-description {
          font-size: 0.9rem;
          color: #666;
          margin-bottom: 1rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .card-content {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          flex-grow: 1;
        }

        .course-card {
          display: flex;
          flex-direction: column;
        }

        .section-title h2 {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: clamp(1.3rem, 4vw, 1.5rem);
        }
        
        /* Sections */
        .section {
          padding: 40px 0;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
        }
        
        .section-header {
            width: 90%;
            margin-bottom: 0;
            max-width: 100%;
        }
        
        .section-header h2 {
          font-weight: 700;
          color: #1a1a1a;
        }
        
        .section-header p {
          font-size: 1.2rem;
          color: #666;
          max-width: 600px;
          margin: 0 auto;
        }
          
        
        /* Scroll Container */
        .scroll-container-wrapper {
          position: relative;
          width: 100%;
          padding: 0 2%; 
          overflow: hidden;
        }
        
        .scroll-container {
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;
          overflow: hidden;
        }
        
        .cards-grid {
          display: flex;
          overflow-x: auto;
          scroll-behavior: smooth;
          gap: 1rem;
          margin-top: 0;
          padding: 1rem 0;
          scrollbar-width: thin;
          scrollbar-color: #0066ff transparent;
          width: 100%;
          -webkit-overflow-scrolling: touch;
          scroll-snap-type: x mandatory;
        }
        
        .cards-grid::-webkit-scrollbar {
          height: 6px;
        }
        
        .cards-grid::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .cards-grid::-webkit-scrollbar-thumb {
          background: #0066ff;
          border-radius: 3px;
        }
        
        .scroll-btn {
          background: white;
          border: 2px solid #e6e6e6;
          border-radius: 100%;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          cursor: pointer;
          transition: all 0.2s;
          position: absolute;
          z-index: 2;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          flex-shrink: 0;
          padding: 0;
        }
        
        .scroll-btn:hover {
          border-color: #0066ff;
          color: #0066ff;
        }
        
        .scroll-btn.left {
          left: -25px;
        }
        
        .scroll-btn.right {
          right: -25px;
        }
        
        /* Scroll Indicators */
        .scroll-indicator {
          position: absolute;
          top: 0;
          bottom: 0;
          width: 40px;
          pointer-events: none;
          z-index: 1;
        }
        
        .scroll-indicator.left-fade {
          left: 0;
        }
        
        .scroll-indicator.right-fade {
          right: 0;
        }
        
        .scroll-hint {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 1rem;
          color: #0066ff;
          font-weight: 500;
          font-size: 0.9rem;
          animation: bounce 2s infinite;
        }
        
        .scroll-arrow {
          font-size: 1.2rem;
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateX(0);
          }
          40% {
            transform: translateX(10px);
          }
          60% {
            transform: translateX(5px);
          }
        }
        
        /* Cards */
        .course-card, .video-card, .quiz-card {
          flex: 0 0 auto;
          width: 280px;
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          transition: all 0.3s ease;
          scroll-snap-align: start;
        }
        
        .course-card:hover, .video-card:hover, .quiz-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 8px 30px rgba(0,0,0,0.12);
        }
        
        .card-image {
          position: relative;
          height: 160px;
          overflow: hidden;
        }
        
        .card-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s;
        }

         .card-image-video img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s;
        }
        
        .course-card:hover .card-image img,
        .video-card:hover .card-image img,
        .quiz-card:hover .card-image img {
          transform: scale(1.05);
        }
        
        .card-badge, .difficulty-badge {
          position: absolute;
          top: 12px;
          left: 12px;
          background: rgba(255,255,255,0.95);
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
        }
        
        .difficulty-badge.easy {
          color: #00cc88;
        }
        
        .difficulty-badge.medium {
          color: #ffaa00;
        }
        
        .difficulty-badge.hard {
          color: #ff4444;
        }
        
        .duration {
          position: absolute;
          bottom: 12px;
          right: 12px;
          background: rgba(0,0,0,0.8);
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
        }
        
        .play-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.2s;
        }
        
        .video-card:hover .play-overlay {
          opacity: 1;
        }
        
        .play-icon {
          width: 50px;
          height: 50px;
          background: rgba(255,255,255,0.9);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          color: #0066ff;
        }
        
        .card-content {
          padding: 1.25rem;
        }
        
        .card-content h3 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.8rem;
          color: #1a1a1a;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .card-meta {
          display: flex;
          justify-content: space-between;
          font-size: 0.85rem;
          color: #666;
          margin-bottom: 1rem;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        
        .level-badge {
          display: inline-block;
          background: #f0f7ff;
          color: #0066ff;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-bottom: 1rem;
        }
        
        .quiz-stats {
          margin-bottom: 1rem;
        }
        
        .taken-count {
          font-size: 0.9rem;
          color: #666;
        }
        
        .card-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        
        .btn-card, .btn-card-outline {
          padding: 8px 16px;
          border-radius: 6px;
          font-weight: 600;
          font-size: 0.85rem;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
          min-width: fit-content;
        }
        
        .btn-card {
          background: #0066ff;
          color: white;
        }
        
        .btn-card:hover {
          background: #0052cc;
        }
        
        .btn-card-outline {
          background: transparent;
          border: 2px solid #0066ff;
          color: #0066ff;
        }
        
        .btn-card-outline:hover {
          background: #0066ff;
          color: white;
        }
        
        .progress-text {
          font-size: 0.85rem;
          color: #00cc88;
          font-weight: 600;
        }
        
        /* CTA Section */
        .cta-section {
          background: linear-gradient(135deg, #333 0%, #212121 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 60px 5%;
        }

        .cta-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        max-width: 800px;
        width: 100%;
        }
        
        .cta-content h2 {
          font-size: clamp(1.8rem, 4vw, 2.5rem);
          margin-bottom: 1rem;
        }
        
        .cta-content p {
          font-size: clamp(1rem, 2.5vw, 1.2rem);
          margin-bottom: 2rem;
          opacity: 0.9;
        }
        
        .cta-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
          width: 100%;
        }
      
        /* Video Modal Styles */
        .video-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.9);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 10px;
          backdrop-filter: blur(10px);
        }

        .video-modal-content {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 95vw;
          max-height: 95vh;
          overflow-y: auto;
          position: relative;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          animation: modalSlideIn 0.3s ease-out;
        }

        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        .modal-close-btn {
          position: absolute;
          top: 10px;
          right: 10px;
          background: rgba(0, 0, 0, 0.7);
          color: white;
          border: none;
          width: 36px;
          height: 36px;
          border-radius: 50%;
          font-size: 1.2rem;
          cursor: pointer;
          z-index: 10;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .modal-close-btn:hover {
          background: rgba(0, 0, 0, 0.9);
          transform: scale(1.1);
        }

        .video-player-container {
          padding: 0;
        }

        .video-header {
          padding: 1.5rem 1.5rem 1rem;
          border-bottom: 1px solid #eee;
        }

        .video-modal-title {
          font-size: clamp(1.2rem, 4vw, 1.5rem);
          font-weight: 700;
          margin-bottom: 1rem;
          color: #1a1a1a;
        }

        .video-meta {
          display: flex;
          gap: 1rem;
          align-items: center;
          flex-wrap: wrap;
        }

        .video-player {
          width: 100%;
          background: #000;
          position: relative;
        }

        /* Vimeo Player Styles */
        .vimeo-player {
          width: 100% !important;
          height: 300px !important;
          border: none !important;
        }

        .video-details {
          padding: 1.5rem;
        }

        .video-details h3 {
          font-size: 1.2rem;
          margin-bottom: 1rem;
          color: #1a1a1a;
        }

        .video-description {
          line-height: 1.6;
          color: #666;
          margin-bottom: 2rem;
        }

        .video-actions {
          display: flex;
          gap: 1rem;
          margin-bottom: 2rem;
          flex-wrap: wrap;
        }

        .video-stats {
          display: flex;
          gap: 2rem;
          flex-wrap: wrap;
          padding-top: 1rem;
          border-top: 1px solid #eee;
        }

        .stat {
          color: #666;
          font-size: 0.9rem;
        }

        .modal-loading,
        .modal-error {
          padding: 3rem 1.5rem;
          text-align: center;
        }

        .error-icon {
          font-size: 2.5rem;
          margin-bottom: 1rem;
        }

        .modal-error h3 {
          margin-bottom: 1rem;
          color: #ff4444;
        }

        /* Enhanced Video Card Styles */
        .video-card {
          position: relative;
          transition: all 0.3s ease;
          border: 1px solid #f0f0f0;
        }

        .video-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 40px rgba(0, 102, 255, 0.15);
        }

        .card-image-video {
          position: relative;
          height: 160px;
          overflow: hidden;
        }

        .video-card .play-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .video-card:hover .play-overlay {
          opacity: 1;
        }

        .video-card .play-icon {
          width: 50px;
          height: 50px;
          background: rgba(255, 255, 255, 0.9);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.1rem;
          color: #0066ff;
          transform: scale(0.9);
          transition: transform 0.3s ease;
        }

        .video-card:hover .play-icon {
          transform: scale(1);
        }

        .video-card-description {
          font-size: 0.85rem;
          color: #666;
          margin-bottom: 1rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.4;
        }

        .views {
          color: #888;
          font-size: 0.8rem;
        }

        /* See More Card Styles */
        .see-more-card {
          flex: 0 0 auto;
          width: 280px;
          background: linear-gradient(135deg, #0066ff, #0044cc);
          color: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 340px;
          scroll-snap-align: start;
        }

        .see-more-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 8px 30px rgba(0,0,0,0.12);
          background: linear-gradient(135deg, #0044cc, #003399);
        }

        .see-more-content {
          text-align: center;
          padding: 1.5rem;
        }

        .see-more-icon {
          font-size: 2.5rem;
          margin-bottom: 1rem;
          font-weight: 300;
        }

        .see-more-content h3 {
          font-size: 1.3rem;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }

        .see-more-content p {
          margin-bottom: 1.5rem;
          opacity: 0.9;
          font-size: 0.9rem;
        }

        .see-more-arrow {
          font-size: 1.8rem;
          animation: bounce 2s infinite;
        }

        /* Premium badge styles */
        .premium-badge.crown {
          position: absolute;
          top: 10px;
          right: 10px;
          background: linear-gradient(135deg, #FFD700, #FFA500);
          color: #000;
          padding: 6px 12px;
          border-radius: 16px;
          font-size: 0.65rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);
          border: 2px solid #FFF;
          z-index: 2;
          animation: float 3s ease-in-out infinite;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .premium-badge.crown::before {
          content: "👑";
          font-size: 0.65rem;
        }

        .premium-badge.crown::after {
          content: "PREMIUM";
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-3px); }
        }

        /* Enhanced Modern Video Modal Styles */
        .video-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 10px;
          backdrop-filter: blur(10px);
          animation: fadeIn 0.3s ease-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .video-modal-content {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 95vw;
          max-height: 95vh;
          overflow: hidden;
          position: relative;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
          animation: modalSlideUp 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          display: flex;
          flex-direction: column;
        }

        @keyframes modalSlideUp {
          from {
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        /* Modern Header */
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 1.5rem 1.5rem 1rem;
          border-bottom: 1px solid #f0f0f0;
          background: white;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .modal-title-section {
          flex: 1;
          margin-right: 1rem;
        }

        .video-modal-title {
          font-size: clamp(1.2rem, 4vw, 1.5rem);
          font-weight: 700;
          margin-bottom: 0.75rem;
          color: #1a1a1a;
          line-height: 1.3;
        }

        .video-meta-tags {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .meta-tag {
          padding: 0.3rem 0.6rem;
          border-radius: 16px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .meta-tag.level {
          background: #e3f2fd;
          color: #1976d2;
        }

        .meta-tag.duration {
          background: #f3e5f5;
          color: #7b1fa2;
        }

        .meta-tag.category {
          background: #e8f5e8;
          color: #388e3c;
        }

        .meta-tag.premium {
          background: linear-gradient(135deg, #FFD700, #FFA500);
          color: #000;
          font-weight: 700;
        }

        .modal-close-btn {
          background: #f8f9fa;
          border: none;
          width: 40px;
          height: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          color: #666;
          flex-shrink: 0;
        }

        .modal-close-btn:hover {
          background: #e9ecef;
          color: #333;
          transform: rotate(90deg);
        }

        /* Modal Body */
        .modal-body {
          flex: 1;
          overflow-y: auto;
          background: white;
        }

        /* Loading State */
        .modal-loading-state {
          padding: 3rem 1.5rem;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }

        .loading-spinner-large {
          width: 50px;
          height: 50px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #0066ff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        /* Error State */
        .modal-error-state {
          padding: 2rem 1.5rem;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.5rem;
        }

        .error-illustration {
          color: #ff4444;
          margin-bottom: 0.5rem;
        }

        .modal-error-state h3 {
          color: #ff4444;
          font-size: 1.3rem;
          margin-bottom: 0.5rem;
        }

        .modal-error-state p {
          color: #666;
          margin-bottom: 1rem;
          font-size: 0.9rem;
        }

        /* Video Content Container */
        .video-content-container {
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        /* Fixed Video Player Container */
        .video-player-wrapper {
          background: #000;
          position: relative;
          width: 100%;
        }

        .video-player-container {
          position: relative;
          width: 100%;
          height: 0;
          padding-bottom: 56.25%; /* 16:9 aspect ratio */
          overflow: hidden;
        }

        .vimeo-player-modern {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          width: 100% !important;
          height: 100% !important;
          border: none !important;
        }

        .video-unavailable {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: #f8f9fa;
          color: #666;
          text-align: center;
          padding: 1.5rem;
        }

        .unavailable-illustration {
          color: #999;
          margin-bottom: 1rem;
        }

        .video-unavailable h4 {
          margin-bottom: 0.5rem;
          color: #333;
          font-size: 1.1rem;
        }

        .video-unavailable p {
          font-size: 0.9rem;
        }

        /* Video Information Panel */
        .video-info-panel {
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .info-section h3 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 1rem;
          color: #1a1a1a;
        }

        .video-description-modern {
          line-height: 1.6;
          color: #666;
          font-size: 0.95rem;
        }

        /* Action Section */
        .action-section {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .action-buttons {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 10px 20px;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          transition: all 0.2s;
          border: none;
          cursor: pointer;
          font-size: 0.9rem;
          min-width: fit-content;
        }

        .btn-primary {
          background: #0066ff;
          color: white;
        }

        .btn-primary:hover {
          background: #0052cc;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 102, 255, 0.3);
        }

        .btn-outline {
          background: transparent;
          border: 2px solid #e0e0e0;
          color: #666;
        }

        .btn-outline:hover {
          border-color: #0066ff;
          color: #0066ff;
          background: #f8fbff;
        }

        .btn-large {
          padding: 12px 24px;
          font-size: 1rem;
        }

        /* Modern Video Stats */
        .video-stats-modern {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
          gap: 1rem;
          padding: 1.25rem;
          background: #f8f9fa;
          border-radius: 10px;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .stat-label {
          font-size: 0.75rem;
          color: #666;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.25rem;
        }

        .stat-value {
          font-size: 0.9rem;
          font-weight: 600;
          color: #1a1a1a;
        }

        .stat-item.premium .stat-value {
          color: #d4af37;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .premium-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          font-weight: 700;
        }

        /* Premium badge for video cards */
        .video-card .premium-badge.crown {
          position: absolute;
          top: 10px;
          right: 10px;
          background: linear-gradient(135deg, #FFD700, #FFA500);
          color: #000;
          padding: 5px 10px;
          border-radius: 14px;
          font-size: 0.65rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);
          border: 2px solid #FFF;
          z-index: 2;
          animation: float 3s ease-in-out infinite;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .video-card .premium-badge.crown::before {
          content: "👑";
          font-size: 0.65rem;
        }

        .video-card .premium-badge.crown::after {
          content: "PREMIUM";
        }

        /* Ensure the card badge (category) doesn't overlap with premium badge */
        .video-card .card-badge {
          position: absolute;
          top: 10px;
          left: 10px;
          background: rgba(255,255,255,0.95);
          padding: 4px 10px;
          border-radius: 16px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          z-index: 2;
        }

        /* Hover effects for premium cards */
        .video-card:hover .premium-badge.crown {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(255, 215, 0, 0.6);
        }

        /* ===== RESPONSIVE BREAKPOINTS ===== */

        /* Tablets and larger phones */
        @media (min-width: 768px) {
          .hero-carousel {
            height: 500px;
            padding: 0; /* Ensure no padding on tablets too */
          }

          .course-container {
            padding: 0 8%;
          }

          .scroll-container-wrapper {
            padding: 0 8%;
          }

          .cards-grid {
            gap: 1.5rem;
          }

          .course-card, .video-card, .quiz-card, .see-more-card {
            width: 300px;
          }

          .scrolling-btn {
            width: 48px;
            height: 48px;
            font-size: 1.8rem;
          }

          .section {
            padding: 60px 0;
          }

          .cta-section {
            padding: 80px 8%;
          }

          .video-modal-content {
            max-width: 90vw;
          }

          .modal-header {
            padding: 2rem 2rem 1.5rem;
          }

          .video-info-panel {
            padding: 2rem;
          }
        }

        /* Desktop */
        @media (min-width: 1024px) {
          .hero-carousel {
            height: 600px;
            padding: 0; /* Ensure no padding on desktop */
          }

          .course-container {
            padding: 0 10%;
          }

          .scroll-container-wrapper {
            padding: 0 10%;
          }

          .cards-grid {
            gap: 2rem;
          }

          .course-card, .video-card, .quiz-card, .see-more-card {
            width: 320px;
          }

          .scrolling-btn {
            width: 54px;
            height: 54px;
            font-size: 2.2rem;
          }

          .section {
            padding: 80px 0;
          }

          .video-modal-content {
            max-width: 900px;
          }
        }

        /* Small phones */
        @media (max-width: 480px) {
          .hero-carousel {
            padding: 0; /* Remove padding on small screens */
            margin: 0; /* Remove margin on small screens */
          }

          .course-container {
            padding: 0 4%;
            margin-bottom: -60px;
          }

          .scroll-container-wrapper {
            padding: 0 4%;
          }

          .cards-grid {
            gap: 0.8rem;
          }

          .course-card, .video-card, .quiz-card, .see-more-card {
            width: 260px;
          }

          .scrolling-btn {
            width: 40px;
            height: 40px;
            font-size: 1.3rem;
            margin-left: 6px;
          }

          .section-title h2 {
            font-size: 1.2rem;
          }

          .card-content {
            padding: 1rem;
          }

          .card-image, .card-image-video {
            height: 140px;
          }

          .cta-actions {
            flex-direction: column;
            width: 100%;
          }

          .cta-actions .btn {
            width: 100%;
          }

          .action-buttons {
            flex-direction: column;
          }

          .action-buttons .btn {
            width: 100%;
          }

          .video-stats-modern {
            grid-template-columns: 1fr;
            gap: 0.75rem;
          }
        }

        /* Extra small phones */
        @media (max-width: 360px) {
          .hero-carousel {
            padding: 0; /* Remove padding on extra small screens */
            margin: 0; /* Remove margin on extra small screens */
          }

          .course-container {
            padding: 0 3%;
            margin-bottom: -50px;
          }

          .scroll-container-wrapper {
            padding: 0 3%;
          }

          .course-card, .video-card, .quiz-card, .see-more-card {
            width: 240px;
          }

          .scrolling-btn {
            width: 36px;
            height: 36px;
            font-size: 1.2rem;
            margin-left: 4px;
          }

          .section-title h2 {
            font-size: 1.1rem;
          }

          .card-content h3 {
            font-size: 1rem;
          }

          .btn-card, .btn-card-outline {
            padding: 6px 12px;
            font-size: 0.8rem;
          }
        }

        /* Hide scroll buttons on very small screens */
        @media (max-width: 320px) {
          .scroll-buttons-container {
            display: none;
          }
        }

        /* Landscape orientation for mobile */
        @media (max-height: 500px) and (orientation: landscape) {
          .hero-carousel {
            height: 100vh;
            min-height: 300px;
            padding: 0; /* Remove padding in landscape */
          }

          .carousel-content h1 {
            font-size: 2rem;
            font-family: 'Urbanist';
          }

          .carousel-content p {
            font-size: 1rem;
            margin-bottom: 1rem;
          }

          .video-modal-content {
            max-height: 98vh;
          }
        }

        /* iPad Pro and larger tablets */
        @media (min-width: 1024px) and (max-width: 1366px) {
          .hero-carousel {
            padding: 0; /* Remove padding on iPad Pro */
          }

          .course-container {
            padding: 0 5%;
          }

          .scroll-container-wrapper {
            padding: 0 5%;
          }
        }

        /* Prevent horizontal overflow in all cases */
        html, body {
          overflow-x: hidden;
          width: 100%;
          position: relative;
        }

        /* Smooth scrolling for iOS */
        .cards-grid {
          -webkit-overflow-scrolling: touch;
        }

        /* Prevent text size adjustment on orientation change */
        html {
          -webkit-text-size-adjust: 100%;
        }
        
      `}</style>
    </div>
  );
};

export default Home;